/**
 * Chaos-to-DSL Conversion Hook
 *
 * FERP CRITICAL: Automatic schema import from unstructured documents
 * Domain-agnostic chaos ingestion and metadata DSL conversion
 *
 * Principles:
 * - All conversion logic from backend (ChaosIngestionService)
 * - AI-powered schema extraction from chaos documents
 * - Confidence scoring for extracted metadata
 * - Repository pattern strict enforcement (no direct API calls)
 * - Life-critical validation during conversion
 * - Immutable audit trail for conversions
 * - 16 primitive types mapping
 */

import { useState, useCallback } from 'react';
import { useCurrentTenant } from './useCurrentTenant';
import { useAuth } from '../security/useAuth';
import { notifications } from '@mantine/notifications';
import { useGraphQLClient } from './useGraphQLClient';
import { chaosDocumentRepository } from '../data/repositories';
import type { ConversionResult as RepoConversionResult } from '../data/repositories/ChaosDocumentRepository';

// ============================================================
// GraphQL Mutations (Direct GraphQL for file upload only)
// ============================================================
// NOTE: convertChaosToDSL is now in repository
// Only UPLOAD_CHAOS_DOCUMENT remains as direct GraphQL (requires base64 file content)

const CONVERT_CHAOS_TO_DSL_LEGACY = `
  mutation ConvertChaosToDSL($input: ConvertChaosToDSLInput!) {
    convertChaosToDSL(input: $input) {
      template {
        template_id
        template_version
        tenant
        business {
          name
          description
          domain
          category
          subcategory
          icon
          color
          tags
        }
        ui {
          layout
          steps {
            id
            label
            description
            fields {
              id
              label
              type
              required
              default_value
              validation
              help_text
            }
          }
        }
        primitives {
          workflow_definitions {
            id
            name
            version
            states
            transitions
          }
          actors {
            id
            name
            role
            permissions
            certifications
          }
          policies {
            id
            name
            type
            conditions
            actions
          }
        }
        mappings {
          ui_to_primitive
          primitive_to_ui
          business_to_technical
          technical_to_business
        }
        compliance {
          frameworks
          certifications
          audit_requirements
          retention_policies
        }
      }
      evidence {
        field_evidence
        primitive_evidence
        compliance_evidence
      }
      confidence {
        overall
        business_metadata
        ui_structure
        primitive_mapping
        field_mapping
        compliance_detection
      }
      extraction_id
      requires_human_review
      review_items {
        field_path
        issue
        suggestion
        confidence
      }
    }
  }
`;

const UPLOAD_CHAOS_DOCUMENT = `
  mutation UploadChaosDocument($input: UploadChaosDocumentInput!) {
    uploadChaosDocument(input: $input) {
      document_id
      filename
      file_type
      size_bytes
      upload_status
      detected_domain
      detected_complexity
      estimated_conversion_time_seconds
    }
  }
`;

const GET_CONVERSION_STATUS = `
  query GetConversionStatus($extractionId: String!, $tenant: String!) {
    getConversionStatus(extractionId: $extractionId, tenant: $tenant) {
      extraction_id
      status
      progress_percentage
      current_phase
      phases_completed
      total_phases
      estimated_time_remaining_seconds
      errors
    }
  }
`;

// ============================================================
// TypeScript Types (Backend Alignment)
// ============================================================

export interface UIField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  default_value?: any;
  validation?: any;
  help_text?: string;
}

export interface UIStep {
  id: string;
  label: string;
  description?: string;
  fields: UIField[];
}

export interface WorkflowDefinitionPrimitive {
  id: string;
  name: string;
  version: string;
  states: string[];
  transitions: any[];
}

export interface ActorPrimitive {
  id: string;
  name: string;
  role: string;
  permissions: string[];
  certifications: string[];
}

export interface PolicyPrimitive {
  id: string;
  name: string;
  type: string;
  conditions: any;
  actions: any;
}

export interface ChaosTemplateMetadata {
  template_id: string;
  template_version: string;
  tenant: string;
  business: {
    name: string;
    description: string;
    domain: string;
    category: string;
    subcategory: string;
    icon: string;
    color: string;
    tags: string[];
  };
  ui: {
    layout: string;
    steps: UIStep[];
  };
  primitives: {
    workflow_definitions: WorkflowDefinitionPrimitive[];
    actors: ActorPrimitive[];
    policies: PolicyPrimitive[];
  };
  mappings: {
    ui_to_primitive: Record<string, any>;
    primitive_to_ui: Record<string, any>;
    business_to_technical: Record<string, any>;
    technical_to_business: Record<string, any>;
  };
  compliance: {
    frameworks: string[];
    certifications: string[];
    audit_requirements: string[];
    retention_policies: any[];
  };
}

export interface ConfidenceMetrics {
  overall: number;
  business_metadata: number;
  ui_structure: number;
  primitive_mapping: number;
  field_mapping: number;
  compliance_detection: number;
}

export interface ReviewItem {
  field_path: string;
  issue: string;
  suggestion: string;
  confidence: number;
}

export interface ConversionResult {
  template: ChaosTemplateMetadata;
  evidence: Record<string, any>;
  confidence: ConfidenceMetrics;
  extraction_id: string;
  requires_human_review: boolean;
  review_items: ReviewItem[];
}

export interface ConvertChaosToDSLInput {
  tenant: string;
  chaos_data?: any;
  document_id?: string;
  target_domain?: string;
  strict_validation?: boolean;
  include_evidence?: boolean;
}

export interface UploadedDocument {
  document_id: string;
  filename: string;
  file_type: string;
  size_bytes: number;
  upload_status: string;
  detected_domain: string;
  detected_complexity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  estimated_conversion_time_seconds: number;
}

// Hook-specific UploadChaosDocumentInput (includes file_content for direct GraphQL upload)
// This is DIFFERENT from ChaosDocumentRepository.UploadChaosDocumentInput
// because the repository interface matches backend metadata-only mutation
export interface UploadChaosDocumentInput {
  tenant: string;
  filename: string;
  file_content: string; // Base64 encoded - used in direct GraphQL mutation
  file_type: string;
  source_system?: string;
}

export interface ConversionStatus {
  extraction_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress_percentage: number;
  current_phase: string;
  phases_completed: number;
  total_phases: number;
  estimated_time_remaining_seconds: number;
  errors: string[];
}

// ============================================================
// Hook Implementation
// ============================================================

export function useConvertChaos() {
  const { tenantId } = useCurrentTenant();
  const { user } = useAuth();
  const client = useGraphQLClient(); // Still needed for uploadDocument (not yet in repository)

  const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null);
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocument | null>(null);
  const [conversionStatus, setConversionStatus] = useState<ConversionStatus | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Upload chaos document for later conversion
   *
   * ARCHITECTURE NOTE: Uses direct GraphQL (NOT repository pattern)
   * WHY: File upload requires base64 encoding and S3 presigned URL flow
   *      Backend GraphQL mutation only accepts metadata (no fileContent field)
   *      See: graphql_gateway/src/metadata_api/chaos_to_dsl_resolvers.rs:411
   *
   * TODO: Migrate to repository when backend implements S3 presigned URL flow:
   *   1. Frontend gets presigned URL from backend
   *   2. Frontend uploads file directly to S3
   *   3. Frontend calls repository.uploadDocument() with S3 key
   *
   * CRITICAL: Handles file upload with progress tracking
   */
  const uploadDocument = useCallback(async (
    file: File
  ): Promise<UploadedDocument> => {
    setIsUploading(true);
    setError(null);

    try {
      // Guard against null tenantId
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      // Read file as base64
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result?.toString().split(',')[1];
          resolve(base64 || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const input: UploadChaosDocumentInput = {
        tenant: tenantId,
        filename: file.name,
        file_content: fileContent,
        file_type: file.type
      };

      const result = await client.mutate({
        mutation: UPLOAD_CHAOS_DOCUMENT,
        variables: { input }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const document = result.data?.uploadChaosDocument;
      setUploadedDocument(document);

      notifications.show({
        title: 'Document Uploaded',
        message: `${document.filename} uploaded successfully (${document.detected_domain} detected)`,
        color: 'green'
      });

      return document;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to upload document');
      setError(error);

      notifications.show({
        title: 'Upload Failed',
        message: error.message,
        color: 'red'
      });

      throw error;

    } finally {
      setIsUploading(false);
    }
  }, [client, tenantId]);

  /**
   * Convert chaos document to metadata DSL template
   *
   * REPOSITORY PATTERN: Uses chaosDocumentRepository.convertToDSL()
   * CRITICAL: AI-powered extraction with confidence scoring
   *
   * @param documentId - Chaos document ID (required)
   * @param targetDomain - Optional domain override
   * @param strictValidation - Enable strict validation
   * @param includeEvidence - Include evidence attribution
   */
  const convertToDSL = useCallback(async (
    documentId: string,
    targetDomain?: string,
    strictValidation: boolean = false,
    includeEvidence: boolean = true
  ): Promise<ConversionResult> => {
    setIsConverting(true);
    setError(null);

    try {
      // Guard against null tenantId
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const createdBy = user?.id || 'unknown';

      // Use repository pattern
      const repoResult: RepoConversionResult = await chaosDocumentRepository.convertToDSL({
        tenant: tenantId,
        documentId,
        targetDomain,
        strictValidation,
        includeEvidence,
        createdBy
      });

      // Map repository camelCase result to hook's snake_case ConversionResult type
      // NOTE: Backend now emits camelCase (via rename_fields), manual mapping still needed
      // because hook's ConversionResult type uses snake_case field names
      const conversion: ConversionResult = {
        template: {
          template_id: repoResult.template.templateId,
          template_version: repoResult.template.templateVersion,
          tenant: repoResult.template.tenant,
          business: repoResult.template.metadata?.business || {},
          ui: repoResult.template.metadata?.ui || {},
          primitives: repoResult.template.metadata?.primitives || {},
          mappings: repoResult.template.metadata?.mappings || {},
          compliance: repoResult.template.metadata?.compliance || {}
        },
        evidence: repoResult.evidence,
        confidence: {
          overall: repoResult.confidence.overall,
          business_metadata: repoResult.confidence.business,
          ui_structure: repoResult.confidence.ui,
          primitive_mapping: repoResult.confidence.primitives,
          field_mapping: repoResult.confidence.mappings,
          compliance_detection: repoResult.confidence.compliance
        },
        extraction_id: `extract-${documentId}`,
        requires_human_review: repoResult.requiresHumanReview,
        review_items: [] // Would be populated from generation metadata if available
      };

      setConversionResult(conversion);

      // Show notification based on confidence
      if (conversion.confidence.overall >= 0.9) {
        notifications.show({
          title: 'Conversion Complete',
          message: `High confidence conversion (${Math.round(conversion.confidence.overall * 100)}%)`,
          color: 'green'
        });
      } else if (conversion.requires_human_review) {
        notifications.show({
          title: 'Conversion Requires Review',
          message: 'Template generated but requires human review',
          color: 'orange'
        });
      }

      return conversion;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to convert chaos to DSL');
      setError(error);

      notifications.show({
        title: 'Conversion Failed',
        message: error.message,
        color: 'red'
      });

      throw error;

    } finally {
      setIsConverting(false);
    }
  }, [chaosDocumentRepository, tenantId, user]);

  /**
   * Get conversion status for long-running conversions
   */
  const getConversionStatus = useCallback(async (
    extractionId: string
  ): Promise<ConversionStatus> => {
    try {
      const result = await client.query({
        query: GET_CONVERSION_STATUS,
        variables: {
          extractionId,
          tenant: tenantId
        }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const status = result.data?.getConversionStatus;
      setConversionStatus(status);

      return status;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get conversion status');
      setError(error);
      throw error;
    }
  }, [client, tenantId]);

  /**
   * Clear conversion results
   */
  const clearConversion = useCallback(() => {
    setConversionResult(null);
    setUploadedDocument(null);
    setConversionStatus(null);
    setError(null);
  }, []);

  return {
    // State
    conversionResult,
    uploadedDocument,
    conversionStatus,
    isConverting,
    isUploading,
    error,

    // Actions
    uploadDocument,
    convertToDSL,
    getConversionStatus,
    clearConversion
  };
}

/**
 * Specialized hook for conversion confidence analysis
 * Provides confidence breakdown and recommendations
 */
export function useConversionConfidence(conversionResult: ConversionResult | null) {
  if (!conversionResult) {
    return {
      overallConfidence: 0,
      highConfidenceFields: 0,
      lowConfidenceFields: 0,
      requiresReview: false,
      confidenceGrade: 'F',
      recommendations: []
    };
  }

  const { confidence, requires_human_review, review_items } = conversionResult;

  const highConfidenceFields = review_items.filter(item => item.confidence >= 0.9).length;
  const lowConfidenceFields = review_items.filter(item => item.confidence < 0.7).length;

  let confidenceGrade = 'F';
  if (confidence.overall >= 0.9) confidenceGrade = 'A';
  else if (confidence.overall >= 0.8) confidenceGrade = 'B';
  else if (confidence.overall >= 0.7) confidenceGrade = 'C';
  else if (confidence.overall >= 0.6) confidenceGrade = 'D';

  const recommendations: string[] = [];
  if (confidence.business_metadata < 0.8) {
    recommendations.push('Review business metadata (name, description, domain)');
  }
  if (confidence.ui_structure < 0.8) {
    recommendations.push('Verify UI fields and layout structure');
  }
  if (confidence.primitive_mapping < 0.8) {
    recommendations.push('Check primitive type mappings');
  }
  if (confidence.compliance_detection < 0.8) {
    recommendations.push('Validate compliance framework detection');
  }

  return {
    overallConfidence: confidence.overall,
    highConfidenceFields,
    lowConfidenceFields,
    requiresReview: requires_human_review,
    confidenceGrade,
    recommendations
  };
}
